import { useQuery } from '@apollo/client';
import { AutoFixHigh, CreateNewFolder, Sell } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMoveDesign } from '~/api/designs';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { ViewToggle } from '~/components/ViewToggle';
import { Count, CountSeparator } from '~/components/count';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { FolderBreadcrumbs } from '~/components/folder-breadcrumbs';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext, useFolderContext } from '~/contexts';
import type { DesignsOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { assert } from '~/lib/assert';
import { DesignListDocument } from './DesignList.generated';
import {
  BulkActions,
  BulkTagDialog,
  DesignMoveDocument,
  FilterAccordion,
  FilterChips,
  StudioGrid,
  StudioNewDesignButton,
  StudioTable,
  TagsDialog,
} from './components';
import { AddFolderDialog } from './components/StudioAddFolderDialog';
import { DesignFiltersProvider, useDesignFiltersParams, useDesignFiltersReducer } from './context';

export const StudioList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { folderTree } = useFolderContext();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ designFolderId: string }>();
  const designFolderId = Number(params.designFolderId);
  assert(isFinite(designFolderId), 'designFolderId param is not a legit number');

  const [openNewFolderDialog, setOpenNewFolderDialog] = useState(false);
  const [tagDesignDialogOpen, setTagDesignDialogOpen] = useState(false);
  const [moveDesignDialogOpen, setMoveDesignDialogOpen] = useState(false);
  const [moveDesign, moveDesignInfo] = useMoveDesign();
  const [showManageTags, setManageTags] = useState(false);

  const filterParams = useDesignFiltersParams();
  const tags = filterParams.tags;
  const published = filterParams.published;

  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view')?.trim() || 'Grid';
  const { orderBy } = useOrderByParams<DesignsOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const handleSearch = useCallback(
    (search: string) => {
      if (!search) return;
      navigate(`../search?search=${search}&backToFolder=${designFolderId}`);
    },
    [navigate, designFolderId],
  );

  useEffect(() => {
    if (!searchParams.get('view')) {
      setSearchParams(
        (prev) => ({
          ...Object.fromEntries(prev),
          view: 'Grid',
        }),
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const { data, loading } = useQuery(DesignListDocument, {
    variables: {
      condition: { tags, published },
      designFolderId,
      networkId: currentNetwork.id,
      page,
      perPage,
      tags,
      published,
      ...(orderBy && { orderBy }),
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.designFolder.designs.pageInfo),
  });

  const { data: designMoveData } = useQuery(DesignMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveDesignDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      designMoveData?.network?.designFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ??
      [],
    [designMoveData],
  );

  const totalCount = useTotalCount(data?.network?.designFolder.designs.pageInfo.nodesCount);
  const folderCount = useTotalCount(data?.network?.designFolder.children.length);

  const designs = data?.network?.designFolder.designs.nodes ?? [];
  const folders = data?.network?.designFolder.children ?? [];

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  useEffect(() => setSelectedIds([]), [setSelectedIds]);

  const canDestroySelectedDesigns = selectedIds.every((selectedId) => {
    const design = designs.find((design) => design.id === selectedId);
    return design?.canDestroy.value;
  });

  const designTags = useMemo(
    () => data?.network?.designTags.map((x) => x.name),
    [data?.network?.designTags],
  );
  const filterCounts = data?.network?.designFilterInfo;

  const [designFiltersState, designFiltersDispatch] = useDesignFiltersReducer(
    filterParams,
    designTags,
  );

  const filtersActive = useMemo(() => tags.length > 0 || published.length > 0, [tags, published]);

  return (
    <DesignFiltersProvider
      {...designFiltersState}
      clearFilters={() => designFiltersDispatch({ type: 'CLEAR_FILTERS' })}
      reinitialize={(params) => designFiltersDispatch({ type: 'REINITIALIZE', params })}
      setFilter={(value) => designFiltersDispatch({ type: 'SET_FILTER', ...value })}
      toggleFilter={(field) => designFiltersDispatch({ type: 'TOGGLE_FILTER', field })}
      togglePanel={() => designFiltersDispatch({ type: 'TOGGLE_PANEL' })}
    >
      <Toolbar
        titleIcon={<AutoFixHigh />}
        titleText={t('pages.studio.title')}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setManageTags(!showManageTags)}
              startIcon={<Sell />}
              variant="outlined"
            >
              {t('common.tags.manage_tags')}
            </Button>
            <Button
              disabled={!data?.network?.designFolder.canUpdate.value}
              onClick={() => setOpenNewFolderDialog((prev: boolean) => !prev)}
              startIcon={<CreateNewFolder />}
              variant="outlined"
            >
              {t('pages.studio.buttons.create_folder')}
            </Button>
            <StudioNewDesignButton
              canUpdate={data?.network?.designFolder.canUpdate.value || false}
            />
          </Box>
        }
        breadcrumbsLabel={
          data?.network?.designFolder && (
            <FolderBreadcrumbs
              allItems={() =>
                navigate({ pathname: `../${folderTree.id}`, search: location.search })
              }
              label={t('pages.studio.list.all_designs')}
              changeFolder={(id) => navigate({ pathname: `../${id}`, search: location.search })}
              folder={data.network.designFolder}
            />
          )
        }
      />

      <ListPageContainer>
        <FilterSidebar open={designFiltersState.panel.open} persistAs="designList:filterWidth">
          <FilterAccordion filterCounts={filterCounts} />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle
                  active={filtersActive}
                  onToggle={() => designFiltersDispatch({ type: 'TOGGLE_PANEL' })}
                />
                <SearchBar
                  onChange={handleSearch}
                  placeholder={t('pages.studio.search_placeholder')}
                  search=""
                />
              </Box>
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center' }}>
                {selectedIds.length > 0 ? (
                  <BulkActions
                    selectedIds={selectedIds}
                    onTagDesign={() => setTagDesignDialogOpen(true)}
                    onMoveDesign={() => setMoveDesignDialogOpen(true)}
                    canDestroySelectedDesigns={canDestroySelectedDesigns}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: 1,
                    }}
                  >
                    <Count
                      selectedCount={0}
                      totalCount={folderCount}
                      thing="common.pluralize.design_folder_plural"
                    />
                    <CountSeparator />
                  </Box>
                )}
                <Box sx={{ display: 'flex' }}>
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount || 0}
                    thing="pages.studio.thing_plural"
                  />
                </Box>
              </Box>
            </ListActions>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  visibility: filtersActive ? 'visible' : 'hidden',
                }}
              >
                <FilterChips />
              </Box>
              <ViewToggle
                view={view as 'List' | 'Grid'}
                onViewChange={(newView) =>
                  setSearchParams((prev) => ({ ...Object.fromEntries(prev), view: newView }))
                }
              />
            </Box>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {view === 'List' ? (
                  <StudioTable
                    designs={designs}
                    folders={folders}
                    loading={loading}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                  />
                ) : (
                  <StudioGrid
                    designs={designs}
                    folders={folders}
                    loading={loading}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                  />
                )}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
          <MoveToFolderDialog
            editableFolderIds={editableFolderIds}
            onSubmit={async (targetFolderId) => {
              await moveDesign({
                refetchQueries: ['DesignList'],
                variables: {
                  designFolderId: targetFolderId,
                  designIds: selectedIds as number[],
                },
              });
              setSelectedIds([]);
            }}
            loading={moveDesignInfo.loading}
            onClose={() => setMoveDesignDialogOpen(false)}
            open={moveDesignDialogOpen}
            i18nKey="pages.studio.dialogs.move_design.title"
          />
          <AddFolderDialog
            designFolderId={designFolderId}
            openNewFolderDialog={openNewFolderDialog}
            setOpenNewFolderDialog={setOpenNewFolderDialog}
          />
          {showManageTags && <TagsDialog onClose={() => setManageTags(false)} open />}
          {tagDesignDialogOpen && (
            <BulkTagDialog
              designIds={selectedIds as string[]}
              designTags={data?.network?.designTags.map((x) => x.name) || []}
              onClose={() => setTagDesignDialogOpen(false)}
              onComplete={() => setSelectedIds(() => [])}
              open
              tags={tags}
            />
          )}
        </ListContainer>
      </ListPageContainer>
    </DesignFiltersProvider>
  );
};
